// @ts-check
import React, { useEffect, useState } from 'react';

import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import Snackbar from '@material-ui/core/Snackbar';
import SnackbarContent from '@material-ui/core/SnackbarContent';
import CloseIcon from '@material-ui/icons/Close';
import DownIcon from '@material-ui/icons/KeyboardArrowDown';
import UpIcon from '@material-ui/icons/KeyboardArrowUp';
import LWTypography from 'components/common/LWTypography';
import Status from 'components/common/Status';
import isTouchDevice from 'is-touch-device';
import { useLatestNotifications } from 'modules/queries/notification/useCurrent';
import styled from 'styled-components';
import DismissButton from './DismissButton';

const SSnackbarContent = styled(SnackbarContent)`
	padding: 0;
	color: ${({ theme }) => theme.palette.text.primary};
	background-color: ${({ theme }) => theme.palette.common.white};
	border: 2px solid ${({ theme }) => theme.palette.primary.main};
	border-radius: 22px;
	.MuiSnackbarContent-message {
		padding: 0;
		width: 100%;
	}
`;

// This needs to be a pixel perfect 44px tall per kkaminski, so need to hard code in that specific padding
const SHeader = styled(Grid)`
	padding-top: 3px;
	padding-right: ${({ theme }) => theme.spacing(0.5)}px;
	padding-bottom: 3px;
	padding-left: ${({ theme }) => theme.spacing(2)}px;
`;

const SArrowIcon = styled(IconButton)`
	color: ${({ theme }) => theme.palette.secondary.dark};
`;

const SCloseIconButton = styled(IconButton)`
	color: ${({ theme }) => theme.palette.text.primary};
	border: 1px solid ${({ theme }) => theme.palette.divider};
`;

const SStatusContainer = styled(Box)`
	border-top: 1px solid ${({ theme }) => theme.palette.divider};
`;

/**
 * @param {object} props
 * @param {boolean} props.showNotifications
 * @param {() => void} props.toggleShowNotifications
 * @param {number} props.notificationCount
 * @param {import('@liquidweb-dev/club-sauce/public/notifications/index.raw').LWApiPublicNotificationCurrentResultRawI['items']} props.pendingNotifications
 * @param {() => void} props.closeSheet
 * @param {(id: number) => void} props.onDismiss
 */
const Content = ({
	showNotifications,
	toggleShowNotifications,
	notificationCount,
	pendingNotifications,
	closeSheet,
	onDismiss,
}) => (
	<Box>
		<SHeader container alignItems="center" justifyContent="space-between">
			<Grid item container alignItems="center" xs={10}>
				<Grid item>
					<LWTypography variant="body2" bold>
						{`${notificationCount} Asset${
							notificationCount === 1 ? '' : 's'
						} Updating`}
					</LWTypography>
				</Grid>
				<Grid item>
					<SArrowIcon onClick={toggleShowNotifications}>
						{showNotifications ? <DownIcon /> : <UpIcon />}
					</SArrowIcon>
				</Grid>
			</Grid>

			<Grid item>
				<SCloseIconButton onClick={closeSheet}>
					<CloseIcon />
				</SCloseIconButton>
			</Grid>
		</SHeader>
		{showNotifications && (
			<Box width="100%">
				{pendingNotifications.map((notification) => (
					<Notification
						key={notification.id}
						notification={notification}
						onDismiss={onDismiss}
					/>
				))}
			</Box>
		)}
	</Box>
);

/**
 * @param {object} props
 * @param {import('@liquidweb-dev/club-sauce/public/notifications/index.raw').LWApiPublicNotificationCurrentResultRawI['items'][number]} props.notification
 * @param {(id: number) => void} props.onDismiss
 */
const Notification = ({ notification, onDismiss }) => {
	const [showDismiss, setShowDismiss] = useState(false);

	const { last_alert: lastAlert, description, domain } = notification;

	const displayMessage = lastAlert || description;

	return (
		<SStatusContainer
			paddingX={1}
			paddingY={0.5}
			onMouseOver={() => setShowDismiss(true)}
			onFocus={() => setShowDismiss(true)}
			onMouseOut={() => setShowDismiss(false)}
			onBlur={() => setShowDismiss(false)}
		>
			<Grid container direction="row" justifyContent="space-between">
				<Status
					level="pending"
					message={displayMessage}
					// @ts-expect-error
					title={domain}
				/>

				<DismissButton
					visible={isTouchDevice() || showDismiss}
					onClick={() => onDismiss(notification.id)}
				/>
			</Grid>
		</SStatusContainer>
	);
};

const StatusSheet = () => {
	const [isOpen, setIsOpen] = useState(false);
	const [showNotifications, setShowNotifications] = useState(false);
	const [hiddenNotifications, setHiddenNotifications] = useState(
		/** @type {number[]} */ ([]),
	);

	const { data: latestNotifications } = useLatestNotifications(
		{},
		{
			refetchInterval: 10000,
		},
	);

	const pendingNotifications = Object.values(latestNotifications)?.filter(
		(notification) => !hiddenNotifications.includes(notification.id),
	);

	useEffect(() => {
		if (pendingNotifications.length > 0 && !isOpen) {
			setIsOpen(true);
		}

		if (pendingNotifications.length === 0 && isOpen) {
			setIsOpen(false);
		}
	}, [pendingNotifications, isOpen]);

	const toggleShowNotifications = () => {
		setShowNotifications(!showNotifications);
	};

	const handleClose = () => {
		setIsOpen(false);
		setHiddenNotifications(
			Object.values(latestNotifications)?.map(
				(notification) => notification.id,
			),
		);
	};

	const notificationCount = pendingNotifications.length;

	/** @param {number} id */
	const handleDismiss = (id) => {
		setHiddenNotifications((prev) => [...prev, id]);

		if (pendingNotifications.length === 1) {
			setIsOpen(false);
		}
	};

	return (
		<Snackbar
			anchorOrigin={{
				vertical: 'bottom',
				horizontal: 'center',
			}}
			open={isOpen}
		>
			<SSnackbarContent
				message={
					<Content
						showNotifications={showNotifications}
						toggleShowNotifications={toggleShowNotifications}
						notificationCount={notificationCount}
						pendingNotifications={pendingNotifications}
						closeSheet={handleClose}
						onDismiss={handleDismiss}
					/>
				}
			/>
		</Snackbar>
	);
};

export default StatusSheet;
